import { SkyList, SkyText } from "@skypointcloud/skypoint-ui";
import { useId } from "react";
import { useLocation } from "wouter";
import type { OverviewSchedules } from "@shared/types/overview";
import { ROUTES, absolutePath } from "@/lib/routes";
import { SchedulesIcon } from "@/lib/icons";
import { OverviewCard, OverviewLink } from "./OverviewCard";
import { nextFireHover } from "./overviewFormatting";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { formatInstant } from "@/lib/time/displayTime";

type NextFire = OverviewSchedules["nextFires"][number];

/** One row's height; the list is exactly as tall as its rows, so it never scrolls inside a card. */
const ROW_HEIGHT = 56;

const LIST_NAME = "Next imports";

/**
 * Schedules (contract §3, card 3): "N of M on", then the next five fires in a `SkyList`. Choosing
 * a fire opens its schedule.
 *
 * Round 6, W8 (walk: "Runner last seen Oct 01, 09:49 PM UTC" beside "Next imports Fri Oct 2, 2:00
 * AM PDT"): one time rule per screen. Each fire reads in the viewer's display zone, named, as every
 * other instant on the page does. Its hover (`nextFireHover`) is the exact instant in that zone,
 * and below it the schedule's own words ("Fri Sep 25, 6:00 AM PDT") when its zone differs and is
 * not UTC (#1078: a next run, an instant, is never read in UTC).
 *
 * `SkyList` renders a `role="listbox"`, and a listbox with no name is an axe failure and a
 * nameless stop for a screen reader. It is named by the visible caption above it through the
 * library's `ariaLabelledBy` (2.17.0); round 7 retired the 2.4.0 workaround that wrote an
 * `aria-label` into the library's element after every render.
 */
export function SchedulesCard({ schedules }: { schedules: OverviewSchedules }) {
  const viewerZone = useDisplayTz();
  const [, navigate] = useLocation();
  const captionId = useId();

  const fires = schedules.nextFires;

  return (
    <OverviewCard
      id="schedules"
      title="Schedules"
      icon={SchedulesIcon}
      testId="overview-schedules"
      action={<OverviewLink to={ROUTES.schedules}>Open Schedules</OverviewLink>}
    >
      <SkyText size={300} weight="semibold" block testId="overview-schedules-on">
        {`${schedules.enabled.toLocaleString()} of ${schedules.total.toLocaleString()} on`}
      </SkyText>

      {fires.length === 0 ? (
        <SkyText size={200} color="muted" block testId="overview-schedules-none">
          {schedules.enabled > 0 ? "No upcoming run." : "No schedule is on."}
        </SkyText>
      ) : (
        <div>
          <SkyText id={captionId} size={200} color="muted" block>
            {LIST_NAME}
          </SkyText>
          <SkyList<NextFire>
            ariaLabelledBy={captionId}
            items={fires}
            getItemKey={(fire) => fire.scheduleId}
            height={fires.length * ROW_HEIGHT}
            itemHeight={ROW_HEIGHT}
            testId="overview-next-fires"
            onItemClick={(fire) => navigate(absolutePath(`${ROUTES.schedules}/${fire.scheduleId}`))}
            renderItem={(fire) => {
              const text = formatInstant(fire.nextFireAt, viewerZone, "short", { named: true });
              return (
                <div
                  className="flex min-w-0 flex-1 flex-col"
                  data-testid={`overview-next-fire-${fire.scheduleId}`}
                >
                  <SkyText size={300} block truncate>
                    {fire.name}
                  </SkyText>
                  <SkyText
                    size={200}
                    color="muted"
                    font="numeric"
                    block
                    truncate
                    title={nextFireHover(fire.nextFireAt, fire.timezone, viewerZone)}
                  >
                    {text}
                  </SkyText>
                </div>
              );
            }}
          />
        </div>
      )}
    </OverviewCard>
  );
}
